import {
  createActorParticipantId,
  createUserParticipantId,
  resolveParticipantId,
} from '../../../domain'
import {
  PRESENCE_EVENT_TYPES,
  RTC_SESSION_EVENT_TYPES,
} from '../../../ports'
import { RTC_PROVIDERS } from '../../../providerIds'
import { isHumanParticipantIdentityKeyPrefix } from '../participantIdentityContract'
import { createDailyRtcSessionHandle } from './createDailyRtcSessionHandle'

function createEventBus() {
  const listeners = new Set()

  return {
    emit(event) {
      listeners.forEach((listener) => {
        listener(event)
      })
    },
    subscribe(listener) {
      listeners.add(listener)

      return () => {
        listeners.delete(listener)
      }
    },
    clear() {
      listeners.clear()
    },
  }
}

function normalizeOptionalString(value, fallbackValue = null) {
  if (typeof value !== 'string') {
    return fallbackValue
  }

  const normalizedValue = value.trim()

  return normalizedValue || fallbackValue
}

function normalizeSessionId(value) {
  return normalizeOptionalString(value, '')
}

function normalizeLocalParticipantIdentity(localParticipantIdentity = {}) {
  const participantId = resolveParticipantId(localParticipantIdentity)

  if (!participantId) {
    return null
  }

  return {
    participantId,
    userId: normalizeOptionalString(localParticipantIdentity?.userId),
    actorType: normalizeOptionalString(localParticipantIdentity?.actorType),
    actorId: normalizeOptionalString(
      localParticipantIdentity?.actorId ?? localParticipantIdentity?.stableActorId,
    ),
    displayName: normalizeOptionalString(localParticipantIdentity?.displayName),
    profilePicId: normalizeOptionalString(
      localParticipantIdentity?.profilePicId ?? localParticipantIdentity?.avatarImageId,
    ),
    role: normalizeOptionalString(localParticipantIdentity?.role),
  }
}

function parseParticipantMetadata(metadata) {
  if (metadata && typeof metadata === 'object' && !Array.isArray(metadata)) {
    return metadata
  }

  const normalizedMetadata = normalizeOptionalString(metadata)
  if (!normalizedMetadata) {
    return {}
  }

  try {
    const parsedMetadata = JSON.parse(normalizedMetadata)
    if (parsedMetadata && typeof parsedMetadata === 'object' && !Array.isArray(parsedMetadata)) {
      return parsedMetadata
    }
  } catch (error) {
    return {
      rawMetadata: normalizedMetadata,
    }
  }

  return {
    rawMetadata: normalizedMetadata,
  }
}

function parseIdentityKey(identityKey) {
  const normalizedIdentityKey = normalizeOptionalString(identityKey)

  if (!normalizedIdentityKey || !normalizedIdentityKey.includes(':')) {
    return null
  }

  const [rawPrefix, ...rawStableIdParts] = normalizedIdentityKey.split(':')
  const prefix = normalizeOptionalString(rawPrefix)
  const stableId = normalizeOptionalString(rawStableIdParts.join(':'))

  if (!prefix || !stableId) {
    return null
  }

  return {
    prefix,
    stableId,
  }
}

function createDailyTransport(sessionId, metadata = {}, extraTransport = {}) {
  return {
    providerId: RTC_PROVIDERS.dailyCo,
    sessionId,
    dailySessionId: sessionId,
    identityKey: normalizeOptionalString(metadata?.identityKey),
    rawMetadata:
      typeof metadata?.rawMetadata === 'string'
        ? metadata.rawMetadata
        : normalizeOptionalString(metadata?.rawMetadata, '') || '',
    userId: normalizeOptionalString(metadata?.userId),
    profilePicId: normalizeOptionalString(
      metadata?.profilePicId ?? metadata?.avatarImageId,
    ),
    ...extraTransport,
  }
}

function deriveParticipantIdentity({
  sessionId,
  metadata = {},
  isLocal = false,
  localParticipantIdentity = null,
  allowTransportFallback = true,
}) {
  const normalizedLocalIdentity = normalizeLocalParticipantIdentity(localParticipantIdentity)

  if (isLocal && normalizedLocalIdentity) {
    return normalizedLocalIdentity
  }

  const resolvedMetadataParticipantId = resolveParticipantId(metadata)
  if (resolvedMetadataParticipantId) {
    return {
      participantId: resolvedMetadataParticipantId,
      userId: normalizeOptionalString(metadata?.userId),
      actorType: normalizeOptionalString(metadata?.actorType),
      actorId: normalizeOptionalString(metadata?.actorId ?? metadata?.stableActorId),
      role: normalizeOptionalString(metadata?.role),
      displayName: normalizeOptionalString(metadata?.displayName ?? metadata?.name),
      profilePicId: normalizeOptionalString(
        metadata?.profilePicId ?? metadata?.avatarImageId,
      ),
    }
  }

  const parsedIdentityKey = parseIdentityKey(metadata?.identityKey)
  if (parsedIdentityKey) {
    if (isHumanParticipantIdentityKeyPrefix(parsedIdentityKey.prefix)) {
      return {
        participantId: createUserParticipantId(parsedIdentityKey.stableId),
        userId: parsedIdentityKey.stableId,
        actorType: 'user',
        actorId: parsedIdentityKey.stableId,
        role: normalizeOptionalString(metadata?.role, parsedIdentityKey.prefix),
        displayName: normalizeOptionalString(metadata?.displayName ?? metadata?.name),
        profilePicId: normalizeOptionalString(
          metadata?.profilePicId ?? metadata?.avatarImageId,
        ),
      }
    }

    return {
      participantId: createActorParticipantId(
        parsedIdentityKey.prefix,
        parsedIdentityKey.stableId,
      ),
      actorType: parsedIdentityKey.prefix,
      actorId: parsedIdentityKey.stableId,
      role: normalizeOptionalString(metadata?.role, parsedIdentityKey.prefix),
      displayName: normalizeOptionalString(metadata?.displayName ?? metadata?.name),
      profilePicId: normalizeOptionalString(
        metadata?.profilePicId ?? metadata?.avatarImageId,
      ),
    }
  }

  if (!allowTransportFallback || !sessionId) {
    return null
  }

  return {
    participantId: createActorParticipantId('daily-session', sessionId),
    actorType: 'daily-session',
    actorId: sessionId,
    role: normalizeOptionalString(metadata?.role, 'participant'),
    displayName: normalizeOptionalString(metadata?.displayName ?? metadata?.name),
    profilePicId: normalizeOptionalString(
      metadata?.profilePicId ?? metadata?.avatarImageId,
    ),
    transportIdentityFallback: true,
  }
}

function resolveDailyAudioEnabled(snapshotParticipant = {}) {
  if (typeof snapshotParticipant?.audioEnabled === 'boolean') {
    return snapshotParticipant.audioEnabled
  }

  const normalizedMicState = normalizeOptionalString(snapshotParticipant?.micState, '')
  if (normalizedMicState === 'on' || normalizedMicState === 'playable') {
    return true
  }

  if (normalizedMicState === 'off' || normalizedMicState === 'blocked') {
    return false
  }

  return Boolean(snapshotParticipant?.audioTrack ?? snapshotParticipant?.audioTrackId)
}

function resolveDailyVideoEnabled(snapshotParticipant = {}) {
  if (typeof snapshotParticipant?.videoEnabled === 'boolean') {
    return snapshotParticipant.videoEnabled
  }

  const normalizedCameraState = normalizeOptionalString(snapshotParticipant?.cameraState, '')
  if (['playable', 'loading', 'interrupted'].includes(normalizedCameraState)) {
    return true
  }

  if (['off', 'blocked'].includes(normalizedCameraState)) {
    return false
  }

  return Boolean(
    snapshotParticipant?.videoHandle ??
      snapshotParticipant?.videoTrack ??
      snapshotParticipant?.videoTrackId,
  )
}

function normalizeSnapshotParticipant(snapshotParticipant = {}, options = {}) {
  const sessionId = normalizeSessionId(
    snapshotParticipant?.sessionId ??
      snapshotParticipant?.session_id ??
      snapshotParticipant?.id,
  )

  if (!sessionId) {
    return null
  }

  const parsedMetadata = parseParticipantMetadata(
    snapshotParticipant?.metadata ?? snapshotParticipant?.userData,
  )
  const parsedIdentityKey = parseIdentityKey(parsedMetadata?.identityKey)
  const metadata = {
    ...parsedMetadata,
    role:
      normalizeOptionalString(parsedMetadata?.role) ||
      normalizeOptionalString(
        snapshotParticipant?.role ??
          snapshotParticipant?.participantRole ??
          snapshotParticipant?.userRole,
      ) ||
      (parsedIdentityKey && isHumanParticipantIdentityKeyPrefix(parsedIdentityKey.prefix)
        ? null
        : normalizeOptionalString(snapshotParticipant?.membershipRole)),
    displayName:
      normalizeOptionalString(parsedMetadata?.displayName ?? parsedMetadata?.name) ||
      normalizeOptionalString(
        snapshotParticipant?.displayName ??
          snapshotParticipant?.userName ??
          snapshotParticipant?.name,
      ),
  }
  const identity = deriveParticipantIdentity({
    sessionId,
    metadata,
    isLocal: options?.isLocal === true,
    localParticipantIdentity: options?.localParticipantIdentity,
    allowTransportFallback: options?.allowTransportFallback !== false,
  })

  if (!identity?.participantId) {
    return null
  }

  const audioEnabled = resolveDailyAudioEnabled(snapshotParticipant)
  const screenSharing = snapshotParticipant?.screenSharing === true
  const videoEnabled = screenSharing ? true : resolveDailyVideoEnabled(snapshotParticipant)
  const videoHandle =
    snapshotParticipant?.videoHandle ||
    (snapshotParticipant?.videoTrack
      ? { track: snapshotParticipant.videoTrack }
      : null)

  return {
    participantId: identity.participantId,
    userId: identity?.userId || null,
    actorType: identity?.actorType || null,
    actorId: identity?.actorId || null,
    displayName:
      identity?.displayName ||
      normalizeOptionalString(
        snapshotParticipant?.displayName ??
          snapshotParticipant?.userName ??
          snapshotParticipant?.name,
      ) ||
      `Participant ${sessionId}`,
    profilePicId:
      identity?.profilePicId ||
      normalizeOptionalString(metadata?.profilePicId ?? metadata?.avatarImageId),
    role: identity?.role || metadata?.role || 'participant',
    isLocal: options?.isLocal === true,
    connectionState: normalizeOptionalString(
      snapshotParticipant?.connectionState,
      'connected',
    ),
    audioMuted: !audioEnabled,
    videoMuted: !videoEnabled,
    screenSharing,
    videoHandle: videoEnabled ? videoHandle : null,
    renderKey: `daily:${sessionId}:${screenSharing ? 'screen' : 'video'}`,
    transport: createDailyTransport(sessionId, metadata, {
      membershipRole: normalizeOptionalString(snapshotParticipant?.membershipRole),
      transportIdentityFallback: identity?.transportIdentityFallback === true,
    }),
  }
}

export function createDailyVideoBinderPort() {
  return {
    getRenderKey(participant = {}) {
      return (
        normalizeOptionalString(participant?.renderKey) ||
        normalizeOptionalString(participant?.id) ||
        normalizeOptionalString(participant?.participantId) ||
        normalizeOptionalString(
          participant?.transport?.sessionId ?? participant?.transport?.dailySessionId,
        ) ||
        'daily:participant'
      )
    },
    async bindVideo({ videoHandle, element }) {
      const resolvedHandle = videoHandle?.track || videoHandle || null

      if (!resolvedHandle || !element) {
        return
      }

      if (typeof resolvedHandle.attach === 'function') {
        await Promise.resolve(resolvedHandle.attach(element))
      }
    },
    async unbindVideo({ videoHandle, element, participant }) {
      const resolvedHandle =
        videoHandle?.track ||
        videoHandle ||
        participant?.videoHandle?.track ||
        participant?.videoHandle ||
        null

      if (!resolvedHandle || typeof resolvedHandle.detach !== 'function') {
        return
      }

      await Promise.resolve(
        element ? resolvedHandle.detach(element) : resolvedHandle.detach(),
      )
    },
  }
}

export function createDailyRtcPortBundle({
  initialConfig = {},
  sessionHandle = null,
  localParticipantIdentity = null,
} = {}) {
  const ownsSessionHandle = !sessionHandle
  const resolvedSessionHandle =
    sessionHandle || createDailyRtcSessionHandle({ initialConfig })
  const rtcEvents = createEventBus()
  const presenceEvents = createEventBus()

  let currentLocalParticipantIdentity = localParticipantIdentity || null

  function emitParticipantSnapshot(snapshot) {
    const snapshotParticipants = Array.isArray(snapshot)
      ? snapshot
      : Array.isArray(snapshot?.participants)
        ? snapshot.participants
        : []

    const participants = snapshotParticipants
      .map((participant) =>
        normalizeSnapshotParticipant(participant, {
          isLocal: participant?.isLocal === true,
          localParticipantIdentity: currentLocalParticipantIdentity,
        }),
      )
      .filter(Boolean)

    rtcEvents.emit({
      type: RTC_SESSION_EVENT_TYPES.participantsChanged,
      participants,
    })
  }

  const rtcSession = {
    async join(config = {}) {
      if (config && Object.keys(config).length > 0) {
        resolvedSessionHandle.updateConfig(config)
      }

      return resolvedSessionHandle.join()
    },
    async leave() {
      return resolvedSessionHandle.leave()
    },
    async setLocalAudioEnabled(enabled) {
      return resolvedSessionHandle.setAudioEnabled(enabled)
    },
    async setLocalVideoEnabled(enabled) {
      return resolvedSessionHandle.setVideoEnabled(enabled)
    },
    async setDevices(devices = {}) {
      const deviceTasks = []

      if (devices?.cameraDeviceId !== undefined) {
        deviceTasks.push(resolvedSessionHandle.switchCamera(devices.cameraDeviceId))
      }

      if (devices?.microphoneDeviceId !== undefined) {
        deviceTasks.push(
          resolvedSessionHandle.switchMicrophone(devices.microphoneDeviceId),
        )
      }

      if (devices?.playbackDeviceId !== undefined) {
        deviceTasks.push(
          resolvedSessionHandle.setPlaybackDevice(devices.playbackDeviceId),
        )
      }

      await Promise.all(deviceTasks)
      return true
    },
    async startScreenShare() {
      return resolvedSessionHandle.startScreenshare()
    },
    async stopScreenShare() {
      return resolvedSessionHandle.stopScreenshare()
    },
    subscribe(listener) {
      return rtcEvents.subscribe(listener)
    },
  }

  const presence = {
    async connect(config = {}) {
      if (config && Object.keys(config).length > 0) {
        resolvedSessionHandle.updateConfig(config)
      }

      return true
    },
    async disconnect() {
      return true
    },
    async publishLocalParticipantMetadata(metadata = {}) {
      currentLocalParticipantIdentity = {
        ...(currentLocalParticipantIdentity || {}),
        ...metadata,
      }

      const localParticipant = normalizeLocalParticipantIdentity(
        currentLocalParticipantIdentity,
      )

      if (localParticipant) {
        presenceEvents.emit({
          type: PRESENCE_EVENT_TYPES.participantMetadataChanged,
          participant: {
            ...localParticipant,
            isLocal: true,
          },
          metadata: {
            ...localParticipant,
            isLocal: true,
          },
          admitToRoster: false,
        })
      }

      return localParticipant
    },
    subscribe(listener) {
      return presenceEvents.subscribe(listener)
    },
  }

  const compatibilityCallbacks = {
    'participants-updated': (snapshot) => {
      emitParticipantSnapshot(snapshot)
    },
    'connection-state-change': (sessionState, previousState, reason) => {
      rtcEvents.emit({
        type: RTC_SESSION_EVENT_TYPES.stateChanged,
        sessionState,
        previousState,
        reason,
      })
    },
  }

  return {
    sessionHandle: resolvedSessionHandle,
    rtcSession,
    presence,
    videoBinder: createDailyVideoBinderPort(),
    compatibilityCallbacks,
    async destroy() {
      rtcEvents.clear()
      presenceEvents.clear()

      if (ownsSessionHandle) {
        await resolvedSessionHandle.destroy()
      }
    },
  }
}
